import type { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { logWhenReady } from '@/app/analytics';
import type { SignupSponsor } from '../services/signup-api/signup-api';
import { parseLegacySignupParams } from '../services/legacy-params';
import { isUnknownExpressScriptsCarrier, queryWithoutCarrier } from '../services/sponsor-lookup';
import { SIGNUP_ENTRY_PATH } from '../steps';

/** Where the flow noticed the unknown carrier, for the operational log. */
export type UnknownCarrierWhere = 'entry' | 'later_step';

/**
 * Sends an Express Scripts link whose carrier the portal does not know back to
 * the entry page without that carrier, once, so the flow starts over as a
 * plain Express Scripts link and the member searches for their carrier.
 * Returns whether it is doing so, so the caller holds back anything else.
 */
export function useDropUnknownCarrier(
  sponsor: UseQueryResult<SignupSponsor>,
  where: UnknownCarrierWhere,
  active = true,
): boolean {
  const router = useRouter();
  const query = useSearchParams().toString();
  const dropping =
    active &&
    sponsor.isError &&
    sponsor.data === undefined &&
    isUnknownExpressScriptsCarrier(parseLegacySignupParams(query), sponsor.error);
  const left = useRef(false);
  useEffect(() => {
    if (!dropping || left.current) return;
    left.current = true;
    // Nothing from the URL goes into the log.
    void logWhenReady('info', 'galapagos.signup.esi_unknown_carrier_dropped', { where });
    const rest = queryWithoutCarrier(query);
    router.replace(rest ? `${SIGNUP_ENTRY_PATH}?${rest}` : SIGNUP_ENTRY_PATH);
  }, [dropping, query, router, where]);
  return dropping;
}
